import React, { useRef, useEffect, useState, useMemo, useCallback } from 'react'
import { IDentalChart } from '@dataTypes/chartingFeature.types'
import { formatProviderDisplayName } from '@utils/provider-display.utils'
import {
  ActionButton,
  ActionButtonIcons,
  ActionButtons,
  DataTable,
  EmptyState,
  Modal,
  Tooltip,
  type DataTableColumn,
  type DataTableRow,
  type DataTableSortDirection,
} from '@enamelai/design-system'
import { Icon as InfoCircleIcon } from '@enamelai/design-system/icons/Info_circle'
import { Icon as NotesIcon } from '@enamelai/design-system/icons/Notes'
import ChartingDeleteItemModal from '@components/pages/Charting/Modals/ChartingDeleteItemModal.component'
import ChartingDiscardInProgressModal from '@components/pages/Charting/Modals/ChartingDiscardInProgressModal.component'
import ChartingEditChartModal from '@components/pages/Charting/Modals/ChartingEditChartModal.component'
import { GridComponent, RowSelectEventArgs } from '@syncfusion/ej2-react-grids'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import { deleteDentalChart } from '@store/actions'
import {
  setCreateDentalChart,
  setDentalUpdated,
  setPreferCreateDentalChart,
  setSelectedDentalChart,
} from '@store/reducers/charting.reducer'
import { useNotificationToast } from '@context/NotificationToastContext'
import {
  createDentalChartDeletedToast,
  createFailedToDeleteChartToast,
} from '@utils/notificationToast.util'
import {
  formatDentalDataSummary,
  DentalDataSummary,
} from '@utils/dental-data-summary.utils'
import { groupDentalDataToothSurfaceEntries } from '@utils/charting-surface-display.utils'
import { getAlignedDentalEditChartId } from '@utils/active-dental-chart.utils'
import {
  compareChartHistorySortValues,
  handleChartHistoryTableSort,
} from '@utils/charting-history-table-sort.util'
import DentalChartSummaryContent from './DentalChartSummaryContent.component'

interface ChartingHistoryProps {
  onViewNote: (id: string | null, openFirstNote?: boolean) => void
  onSelectHistory: (chart: RowSelectEventArgs) => void
  gridRef: React.RefObject<GridComponent>
  layoutSyncKey?: string
}

type DentalHistorySortColumn =
  | 'date'
  | 'title'
  | 'tooth_surface'
  | 'provider_name'

type DentalHistoryRow = DataTableRow & {
  id: string
  date: string
  dateSortKey: number
  title: string
  tooth_surface: string
  tooth_surface_entries: string[]
  provider_name: string
}

const PAGE_SIZE_OPTIONS = [5, 10, 15, 20]
const HISTORY_ROW_DESELECT_DELAY_MS = 250
const TOOTH_SURFACE_PREVIEW_ENTRY_LIMIT = 5

const emitHistorySelection = (
  onSelectHistory: (chart: RowSelectEventArgs) => void,
  chartId: string | null,
): void => {
  onSelectHistory({
    name: chartId ? 'rowSelected' : 'rowDeselected',
    data: chartId ? { chart_id: chartId } : undefined,
  } as RowSelectEventArgs)
}

const ChartingHistory: React.FC<ChartingHistoryProps> = ({
  onViewNote,
  onSelectHistory,
}) => {
  const isMountedRef = useRef<boolean>(true)
  const rowDeselectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const dispatch = useAppDispatch()
  const { showToast } = useNotificationToast()
  const selectedPatient = useAppSelector(
    (state) => state.patient.selectedPatient,
  )
  const dentalChartsRaw = useAppSelector((state) => state.charting.dentalCharts)
  const dentalCharts = useMemo(() => dentalChartsRaw ?? [], [dentalChartsRaw])
  const selectedDentalChart = useAppSelector(
    (state) => state.charting.selectedDentalChart,
  )
  const chartingLoading = useAppSelector((state) => state.charting.loading)
  const isDentalUpdated = useAppSelector(
    (state) => state.charting.isDentalUpdated,
  )
  const createDentalChart = useAppSelector(
    (state) => state.charting.createDentalChart,
  )
  const preferCreateDentalChart = useAppSelector(
    (state) => state.charting.preferCreateDentalChart,
  )
  const [showEditConfirm, setShowEditConfirm] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [showSummaryModal, setShowSummaryModal] = useState(false)
  const [summaryChartTitle, setSummaryChartTitle] = useState('')
  const [dentalSummary, setDentalSummary] = useState<DentalDataSummary | null>(
    null,
  )
  const [chartToEdit, setChartToEdit] = useState<IDentalChart | null>(null)
  const [chartToDelete, setChartToDelete] = useState<IDentalChart | null>(null)
  const [pendingDeleteChartId, setPendingDeleteChartId] = useState<
    string | null
  >(null)
  const [pendingDeleteChartTitle, setPendingDeleteChartTitle] =
    useState<string>('')
  const [isDeleteInFlight, setIsDeleteInFlight] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(PAGE_SIZE_OPTIONS[0])
  const [sortColumnId, setSortColumnId] =
    useState<DentalHistorySortColumn | null>(null)
  const [sortDirection, setSortDirection] =
    useState<DataTableSortDirection>('asc')
  const hasHandledPendingDeleteRef = useRef<boolean>(false)
  const lastDeleteSuccessToastChartIdRef = useRef<string | null>(null)
  const onViewNoteRef = useRef(onViewNote)
  onViewNoteRef.current = onViewNote
  const onSelectHistoryRef = useRef(onSelectHistory)
  onSelectHistoryRef.current = onSelectHistory
  const editingChartId = useMemo(
    (): string =>
      getAlignedDentalEditChartId(
        selectedDentalChart,
        createDentalChart,
        preferCreateDentalChart,
      ),
    [createDentalChart, preferCreateDentalChart, selectedDentalChart],
  )
  const editingChartIdRef = useRef(editingChartId)
  editingChartIdRef.current = editingChartId

  useEffect(() => {
    isMountedRef.current = true
    return (): void => {
      isMountedRef.current = false
      if (rowDeselectTimerRef.current) {
        clearTimeout(rowDeselectTimerRef.current)
        rowDeselectTimerRef.current = null
      }
    }
  }, [])

  useEffect(() => {
    if (!selectedPatient && isMountedRef.current) {
      dispatch(setSelectedDentalChart(null))
    }
  }, [selectedPatient, dispatch])

  useEffect(() => {
    if (
      selectedPatient &&
      selectedDentalChart &&
      selectedDentalChart.patient_id !== selectedPatient.patient_id
    ) {
      dispatch(setSelectedDentalChart(null))
    }
  }, [selectedPatient, selectedDentalChart, dispatch])

  const historyRows = useMemo((): DentalHistoryRow[] => {
    return selectedPatient && !chartingLoading
      ? dentalCharts
          .filter((entry) => entry.patient_id === selectedPatient.patient_id)
          .map((entry) => {
            const toothSurfaceEntries = groupDentalDataToothSurfaceEntries(
              entry.dental_data,
            )
            return {
              id: entry.chart_id,
              date: new Date(
                entry.date_time_created_at ?? '',
              ).toLocaleDateString(),
              dateSortKey:
                new Date(entry.date_time_created_at ?? '').getTime() || 0,
              title: entry.title ?? '',
              tooth_surface: toothSurfaceEntries.join(', '),
              tooth_surface_entries: toothSurfaceEntries,
              provider_name: formatProviderDisplayName(entry.provider),
            }
          })
      : []
  }, [selectedPatient, chartingLoading, dentalCharts])

  useEffect(() => {
    setCurrentPage(1)
    setSortColumnId(null)
    setSortDirection('asc')
  }, [selectedPatient?.patient_id, pageSize, historyRows.length])

  const getDentalSortValue = useCallback(
    (
      row: DentalHistoryRow,
      columnId: DentalHistorySortColumn,
    ): string | number => {
      switch (columnId) {
        case 'date':
          return row.dateSortKey
        case 'title':
          return row.title
        case 'tooth_surface':
          return row.tooth_surface
        case 'provider_name':
          return row.provider_name
        default:
          return row.title
      }
    },
    [],
  )

  const sortedHistoryRows = useMemo((): DentalHistoryRow[] => {
    if (!sortColumnId) {
      return historyRows
    }
    const sorted = [...historyRows]
    sorted.sort((rowA, rowB) =>
      compareChartHistorySortValues(
        getDentalSortValue(rowA, sortColumnId),
        getDentalSortValue(rowB, sortColumnId),
        sortDirection,
      ),
    )
    return sorted
  }, [getDentalSortValue, historyRows, sortColumnId, sortDirection])

  const totalPages = Math.max(1, Math.ceil(sortedHistoryRows.length / pageSize))
  const pagedRows = useMemo((): DentalHistoryRow[] => {
    const start = (currentPage - 1) * pageSize
    return sortedHistoryRows.slice(start, start + pageSize)
  }, [currentPage, sortedHistoryRows, pageSize])

  useEffect(() => {
    const selectedId = selectedDentalChart?.chart_id?.trim()
    if (!selectedId) return
    const selectedIndex = sortedHistoryRows.findIndex(
      (row) => row.id === selectedId,
    )
    if (selectedIndex < 0) return
    setCurrentPage(Math.floor(selectedIndex / pageSize) + 1)
  }, [pageSize, selectedDentalChart?.chart_id, sortedHistoryRows])

  const handleSort = useCallback(
    (columnId: string): void => {
      handleChartHistoryTableSort(
        columnId,
        sortColumnId,
        sortDirection,
        (value) => setSortColumnId(value as DentalHistorySortColumn | null),
        setSortDirection,
        setCurrentPage,
      )
    },
    [sortColumnId, sortDirection],
  )

  const chartById = useCallback(
    (chartId: string): IDentalChart | undefined =>
      dentalCharts.find((chart) => chart.chart_id === chartId),
    [dentalCharts],
  )

  const clearPendingDeleteState = useCallback((): void => {
    setPendingDeleteChartId(null)
    setPendingDeleteChartTitle('')
    setIsDeleteInFlight(false)
    hasHandledPendingDeleteRef.current = false
  }, [])

  const reconcilePendingDelete = useCallback((): void => {
    if (!pendingDeleteChartId || isDeleteInFlight) return
    if (hasHandledPendingDeleteRef.current) return
    const wasDeleted = !dentalCharts.some(
      (chart) => chart.chart_id === pendingDeleteChartId,
    )
    if (!wasDeleted) return
    hasHandledPendingDeleteRef.current = true
    if (lastDeleteSuccessToastChartIdRef.current === pendingDeleteChartId) {
      clearPendingDeleteState()
      return
    }
    if (selectedDentalChart?.chart_id === pendingDeleteChartId) {
      dispatch(setSelectedDentalChart(null))
    }
    const remaining = historyRows.find((row) => row.id !== pendingDeleteChartId)
    emitHistorySelection(onSelectHistoryRef.current, remaining?.id ?? null)
    showToast(createDentalChartDeletedToast(pendingDeleteChartTitle || 'chart'))
    lastDeleteSuccessToastChartIdRef.current = pendingDeleteChartId
    clearPendingDeleteState()
  }, [
    clearPendingDeleteState,
    dentalCharts,
    dispatch,
    historyRows,
    isDeleteInFlight,
    pendingDeleteChartId,
    pendingDeleteChartTitle,
    selectedDentalChart?.chart_id,
    showToast,
  ])

  useEffect(() => {
    if (!preferCreateDentalChart) {
      return
    }
    setShowEditConfirm(false)
    setChartToEdit(null)
  }, [preferCreateDentalChart])

  useEffect(() => {
    reconcilePendingDelete()
  }, [historyRows.length, reconcilePendingDelete])

  const handleDeleteChart = useCallback((chart: IDentalChart): void => {
    setShowDeleteConfirm(true)
    setChartToDelete(chart)
  }, [])

  const handleEditChart = useCallback((chart: IDentalChart): void => {
    setShowEditConfirm(true)
    setChartToEdit({ ...chart })
  }, [])

  const handleViewSummary = useCallback((chart: IDentalChart): void => {
    setSummaryChartTitle(chart.title ?? 'chart')
    setDentalSummary(formatDentalDataSummary(chart.dental_data))
    setShowSummaryModal(true)
  }, [])

  const confirmEditChart = useCallback(
    (chart: IDentalChart): void => {
      dispatch(setCreateDentalChart({ ...chart }))
      dispatch(setSelectedDentalChart({ ...chart }))
      dispatch(setPreferCreateDentalChart(false))
      dispatch(setDentalUpdated(false))
      setShowEditConfirm(false)
    },
    [dispatch],
  )

  const handleTableClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>): void => {
      if (event.detail > 1) {
        return
      }
      const target = event.target as HTMLElement
      if (
        target.closest(
          'button:not(.charting-history-tooth-surface), a, input, label, [role="menu"]',
        )
      ) {
        return
      }
      const chartId = target
        .closest('tr.ds-data-table__row')
        ?.querySelector<HTMLElement>('[data-chart-history-id]')
        ?.getAttribute('data-chart-history-id')
        ?.trim()
      if (!chartId) {
        return
      }
      const isSelected =
        chartId === (selectedDentalChart?.chart_id?.trim() ?? '')
      if (rowDeselectTimerRef.current) {
        clearTimeout(rowDeselectTimerRef.current)
        rowDeselectTimerRef.current = null
      }
      if (!isSelected) {
        emitHistorySelection(onSelectHistory, chartId)
        return
      }
      rowDeselectTimerRef.current = setTimeout(() => {
        rowDeselectTimerRef.current = null
        emitHistorySelection(onSelectHistory, null)
      }, HISTORY_ROW_DESELECT_DELAY_MS)
    },
    [onSelectHistory, selectedDentalChart?.chart_id],
  )

  const handleTableDoubleClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>): void => {
      const target = event.target as HTMLElement
      if (
        target.closest(
          'button:not(.charting-history-tooth-surface), a, input, label, [role="menu"]',
        )
      ) {
        return
      }
      if (rowDeselectTimerRef.current) {
        clearTimeout(rowDeselectTimerRef.current)
        rowDeselectTimerRef.current = null
      }
      const chartId = target
        .closest('tr.ds-data-table__row')
        ?.querySelector<HTMLElement>('[data-chart-history-id]')
        ?.getAttribute('data-chart-history-id')
        ?.trim()
      if (!chartId) {
        return
      }
      const chart = chartById(chartId)
      if (!chart || !isMountedRef.current) {
        return
      }
      const isSelected =
        chartId === (selectedDentalChart?.chart_id?.trim() ?? '')
      if (!isSelected) {
        emitHistorySelection(onSelectHistory, chartId)
      }
      handleViewSummary(chart)
    },
    [
      chartById,
      handleViewSummary,
      onSelectHistory,
      selectedDentalChart?.chart_id,
    ],
  )

  const columns = useMemo(
    (): DataTableColumn<DentalHistoryRow>[] => [
      {
        id: 'date',
        label: 'Date',
        sortable: true,
        width: 120,
        renderCell: (row): React.ReactNode => (
          <span data-chart-history-id={row.id}>{row.date}</span>
        ),
      },
      {
        id: 'title',
        label: 'Chart name',
        sortable: true,
      },
      {
        id: 'tooth_surface',
        label: 'Tooth/surface',
        sortable: true,
        renderCell: (row): React.ReactNode => {
          const previewEntries = row.tooth_surface_entries.slice(
            0,
            TOOTH_SURFACE_PREVIEW_ENTRY_LIMIT,
          )
          const hiddenEntryCount =
            row.tooth_surface_entries.length - previewEntries.length

          return (
            <Tooltip
              content={row.tooth_surface}
              className="charting-history-tooth-surface-tooltip"
              portal
            >
              <button
                type="button"
                className="charting-history-tooth-surface"
                aria-label={`Tooth and surface details: ${row.tooth_surface}`}
              >
                {previewEntries.map((entry, index) => (
                  <span
                    key={entry}
                    className="charting-history-tooth-surface__entry"
                  >
                    {index > 0 ? ', ' : null}
                    {entry}
                  </span>
                ))}
                {hiddenEntryCount > 0 ? (
                  <span className="charting-history-tooth-surface__more">
                    {' '}
                    +{hiddenEntryCount} more
                  </span>
                ) : null}
              </button>
            </Tooltip>
          )
        },
      },
      {
        id: 'provider_name',
        label: 'Provider',
        sortable: true,
        width: 200,
      },
      {
        id: 'notes',
        label: 'Notes',
        align: 'center',
        width: 72,
        renderCell: (row): React.ReactNode => {
          const chart = chartById(row.id)
          if (!chart?.notes?.length) {
            return <span>-</span>
          }
          return (
            <div
              className="charting-history-notes-action"
              onClick={(event): void => event.stopPropagation()}
            >
              <ActionButton
                icon={
                  <NotesIcon
                    className="ds-action-button__svg"
                    width={16}
                    height={16}
                    aria-hidden
                  />
                }
                label="View notes"
                size="sm"
                tone="primary"
                variant="outline"
                onClick={(): void => {
                  onViewNoteRef.current(row.id, true)
                }}
              />
            </div>
          )
        },
      },
    ],
    [chartById],
  )

  const selectedRowIds = useMemo((): string[] => {
    const selectedId = selectedDentalChart?.chart_id?.trim() ?? ''
    return selectedId ? [selectedId] : []
  }, [selectedDentalChart?.chart_id])

  const showEmptyState = !chartingLoading && historyRows.length === 0

  return (
    <div className="charting-history">
      {!showEmptyState && (
        <div className="charting-history-title mb-1">Charting history</div>
      )}
      <div
        className="history-grid-container"
        onClick={handleTableClick}
        onDoubleClick={handleTableDoubleClick}
      >
        {showEmptyState ? (
          <EmptyState
            className="charting-history-empty"
            title="No dental charts found"
            description={
              selectedPatient
                ? "You don't have any dental charts on record yet."
                : 'Select a patient to view their charts.'
            }
          />
        ) : (
          <DataTable<DentalHistoryRow>
            className="charting-history-table"
            columns={columns}
            rows={pagedRows}
            state={chartingLoading ? 'loading' : 'default'}
            sortColumnId={sortColumnId ?? undefined}
            sortDirection={sortColumnId ? sortDirection : undefined}
            onSort={handleSort}
            selectedRowIds={selectedRowIds}
            actionsLabel="Actions"
            renderActions={(row): React.ReactNode => {
              const chart = chartById(row.id)
              if (!chart) {
                return null
              }
              return (
                <div onClick={(event): void => event.stopPropagation()}>
                  <ActionButtons
                    size="sm"
                    actions={[
                      {
                        id: 'summary',
                        label: 'View dental chart summary',
                        icon: (
                          <InfoCircleIcon
                            className="ds-action-button__svg"
                            width={16}
                            height={16}
                            aria-hidden
                          />
                        ),
                        onClick: (): void => {
                          handleViewSummary(chart)
                        },
                        tone: 'primary',
                        variant: 'outline',
                      },
                      {
                        id: 'edit',
                        label: 'Edit',
                        icon: ActionButtonIcons.edit(),
                        onClick: (): void => {
                          handleEditChart(chart)
                        },
                        tone: 'primary',
                        variant: 'outline',
                        disabled: row.id === editingChartIdRef.current,
                      },
                      {
                        id: 'delete',
                        label: 'Delete',
                        icon: ActionButtonIcons.delete(),
                        onClick: (): void => {
                          handleDeleteChart(chart)
                        },
                        tone: 'danger',
                        variant: 'outline',
                      },
                    ]}
                  />
                </div>
              )
            }}
            pagination={{
              currentPage,
              totalPages,
              pageSize,
              pageSizeOptions: PAGE_SIZE_OPTIONS,
              onPageChange: setCurrentPage,
              onPageSizeChange: (nextSize): void => {
                setPageSize(nextSize)
                setCurrentPage(1)
              },
            }}
          />
        )}
        <Modal
          open={showSummaryModal}
          onClose={() => setShowSummaryModal(false)}
          size="lg"
          title={`Dental chart summary (${summaryChartTitle})`}
          primaryLabel="Close"
          secondaryLabel=""
          onPrimaryClick={() => setShowSummaryModal(false)}
        >
          {dentalSummary ? (
            <DentalChartSummaryContent
              summary={dentalSummary}
              showHeading={false}
            />
          ) : null}
        </Modal>
        <ChartingDeleteItemModal
          show={showDeleteConfirm}
          itemName={chartToDelete?.title ?? ''}
          deleteDisabled={isDeleteInFlight}
          onCancel={() => setShowDeleteConfirm(false)}
          onDelete={() => {
            if (isDeleteInFlight) return
            const chartId = chartToDelete?.chart_id
            const chartTitle = chartToDelete?.title ?? 'chart'
            if (!chartId) {
              setShowDeleteConfirm(false)
              return
            }
            setPendingDeleteChartId(chartId)
            setPendingDeleteChartTitle(chartTitle)
            setIsDeleteInFlight(true)
            hasHandledPendingDeleteRef.current = false
            dispatch(deleteDentalChart(chartId))
              .unwrap()
              .then(() => {
                setIsDeleteInFlight(false)
              })
              .catch((error) => {
                clearPendingDeleteState()
                showToast(
                  createFailedToDeleteChartToast(
                    error instanceof Error ? error.message : undefined,
                  ),
                )
              })
            setShowDeleteConfirm(false)
          }}
        />
        {isDentalUpdated && chartToEdit ? (
          <ChartingDiscardInProgressModal
            show={showEditConfirm}
            chartName={chartToEdit.title ?? ''}
            onKeepEditing={() => setShowEditConfirm(false)}
            onDiscard={() => {
              if (!chartToEdit) return
              confirmEditChart(chartToEdit)
            }}
          />
        ) : (
          <ChartingEditChartModal
            show={showEditConfirm}
            chartName={chartToEdit?.title ?? ''}
            actionVerb="update"
            onCancel={() => setShowEditConfirm(false)}
            onContinue={() => {
              if (!chartToEdit) return
              confirmEditChart(chartToEdit)
            }}
          />
        )}
      </div>
    </div>
  )
}

export default React.memo(ChartingHistory)
